import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote } from "lucide-react";
import { format } from "date-fns";

interface PublicReviewsSectionProps {
  tenantId: string;
}

interface Review {
  id: string;
  overall_rating: number;
  comment: string | null;
  guest_name: string;
  display_name: string | null;
  created_at: string;
  bookings: {
    organization: string | null;
  } | null;
}

const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`h-4 w-4 ${
          star <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"
        }`}
      />
    ))}
  </div>
);

const PublicReviewsSection = ({ tenantId }: PublicReviewsSectionProps) => {
  const { data: reviews, isLoading } = useQuery({
    queryKey: ["public-reviews", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("id, overall_rating, comment, guest_name, display_name, created_at, bookings(organization)")
        .eq("tenant_id", tenantId)
        .eq("is_public", true)
        .order("created_at", { ascending: false })
        .limit(6);

      if (error) throw error;
      return data as Review[];
    },
    enabled: !!tenantId,
  });

  if (isLoading || !reviews?.length) {
    return null;
  }

  const averageRating =
    reviews.reduce((sum, r) => sum + r.overall_rating, 0) / reviews.length;

  return (
    <section className="container mx-auto px-4 py-16 bg-gradient-to-b from-muted/30 to-transparent">
      <div className="text-center mb-12 animate-fade-in">
        <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
          Guest Reviews
        </h2>
        <div className="flex items-center justify-center gap-2 text-lg">
          <StarRating rating={Math.round(averageRating)} />
          <span className="text-muted-foreground">
            {averageRating.toFixed(1)} from {reviews.length} review
            {reviews.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
        {reviews.map((review) => (
          <Card key={review.id} className="bg-background shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Quote className="h-6 w-6 text-primary/20 flex-shrink-0 mt-1" />
                <div className="space-y-2 flex-1">
                  <StarRating rating={review.overall_rating} />
                  {review.comment && (
                    <p className="text-sm text-muted-foreground line-clamp-4">
                      {review.comment}
                    </p>
                  )}
                  <div className="flex items-center justify-between pt-2">
                    <div>
                      <span className="text-sm font-medium">{review.display_name || review.guest_name}</span>
                      {review.bookings?.organization && (
                        <span className="text-xs text-muted-foreground block">{review.bookings.organization}</span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(review.created_at), "MMM yyyy")}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default PublicReviewsSection;
